import { CheckedEnvironment } from "../types";
import { CheckedMobileApp, StoreAppInfo } from "../mobile-apps/types";

export type MockScenarioId =
  | "all-online"
  | "all-offline"
  | "mixed"
  | "empty"
  | "mobile-degraded"
  | "many-environments";

export type MockScenarioData = {
  environments: CheckedEnvironment[];
  mobileApps: CheckedMobileApp[];
};

export type MockScenario = {
  id: MockScenarioId;
  label: string;
  data: MockScenarioData;
};

const onlineStore = (
  platform: "ios" | "android",
  version: string,
): StoreAppInfo => ({
  platform,
  status: "online",
  version,
  lastUpdated: "1 jan. 2026",
  storeUrl:
    platform === "ios"
      ? "https://apps.apple.com/app/id0"
      : "https://play.google.com/store/apps/details?id=app.primio.v2",
});

const offlineStore = (
  platform: "ios" | "android",
  error = "unavailable",
): StoreAppInfo => ({
  platform,
  status: "offline",
  error,
});

const healthyApps: CheckedMobileApp[] = [
  {
    id: "primio",
    name: "Primio",
    ios: onlineStore("ios", "4.2.1"),
    android: onlineStore("android", "4.2.0"),
  },
  {
    id: "zorgexpeditie",
    name: "ZorgExpeditie",
    ios: onlineStore("ios", "2.1.0"),
    android: onlineStore("android", "2.1.0"),
  },
  {
    id: "humio",
    name: "Humio",
    ios: onlineStore("ios", "1.4.3"),
    android: onlineStore("android", "1.4.2"),
  },
];

const offlineApps: CheckedMobileApp[] = healthyApps.map((app) => ({
  id: app.id,
  name: app.name,
  ios: app.ios ? offlineStore("ios") : undefined,
  android: app.android ? offlineStore("android") : undefined,
}));

const degradedApps: CheckedMobileApp[] = [
  {
    id: "primio",
    name: "Primio",
    ios: onlineStore("ios", "4.2.1"),
    android: offlineStore("android", "Play Store lookup failed"),
  },
  {
    id: "zorgexpeditie",
    name: "ZorgExpeditie",
    ios: offlineStore("ios", "App Store lookup failed"),
    android: offlineStore("android", "Play Store lookup failed"),
  },
  {
    id: "humio",
    name: "Humio",
    ios: onlineStore("ios", "1.4.3"),
    android: onlineStore("android", "1.4.2"),
  },
];

const allOnlineEnvs: CheckedEnvironment[] = [
  {
    organisation: "sandbox",
    status: "online",
    cmsStatus: "online",
    version: "1.42.0",
  },
  {
    organisation: "acme",
    status: "online",
    cmsStatus: "online",
    version: "1.41.2",
  },
  {
    organisation: "demo",
    status: "online",
    cmsStatus: "online",
    version: "1.42.0",
  },
];

const allOfflineEnvs: CheckedEnvironment[] = allOnlineEnvs.map((env) => ({
  ...env,
  status: "offline",
  cmsStatus: "offline",
  version: undefined,
}));

const mixedEnvs: CheckedEnvironment[] = [
  {
    organisation: "sandbox",
    status: "online",
    cmsStatus: "online",
    version: "1.42.0",
  },
  {
    organisation: "acme",
    status: "offline",
    cmsStatus: "online",
  },
  {
    organisation: "demo",
    status: "online",
    cmsStatus: "offline",
    version: "1.41.0",
  },
  {
    organisation: "legacy",
    status: "offline",
    cmsStatus: "offline",
  },
];

const MANY_ENV_NAMES = [
  "sandbox",
  "acme",
  "demo",
  "legacy",
  "northwind",
  "contoso",
  "fabrikam",
  "adventure",
  "humio",
  "zorg",
  "atlas",
  "beacon",
  "cinder",
  "drift",
  "ember",
  "flux",
  "grove",
  "harbor",
  "ivory",
  "jade",
  "kepler",
  "lumen",
  "mira",
  "nova",
  "orbit",
  "prism",
  "quill",
  "ridge",
  "sable",
  "tide",
];

const manyEnvs: CheckedEnvironment[] = MANY_ENV_NAMES.map((organisation, i) => {
  const kind = i % 7;
  if (kind === 3) {
    return { organisation, status: "offline", cmsStatus: "online" };
  }
  if (kind === 5) {
    return {
      organisation,
      status: "online",
      cmsStatus: "offline",
      version: `1.${40 + (i % 5)}.${i % 10}`,
    };
  }
  if (kind === 6) {
    return { organisation, status: "offline", cmsStatus: "offline" };
  }
  return {
    organisation,
    status: "online",
    cmsStatus: "online",
    version: `1.${41 + (i % 3)}.${i % 9}`,
  };
});

export const MOCK_SCENARIOS: MockScenario[] = [
  {
    id: "all-online",
    label: "All online",
    data: { environments: allOnlineEnvs, mobileApps: healthyApps },
  },
  {
    id: "all-offline",
    label: "All offline",
    data: { environments: allOfflineEnvs, mobileApps: offlineApps },
  },
  {
    id: "mixed",
    label: "Mixed failures",
    data: { environments: mixedEnvs, mobileApps: healthyApps },
  },
  {
    id: "many-environments",
    label: "Many environments",
    data: { environments: manyEnvs, mobileApps: healthyApps },
  },
  {
    id: "empty",
    label: "Empty",
    data: { environments: [], mobileApps: [] },
  },
  {
    id: "mobile-degraded",
    label: "Mobile degraded",
    data: { environments: allOnlineEnvs, mobileApps: degradedApps },
  },
];

export const DEFAULT_MOCK_SCENARIO_ID: MockScenarioId = "all-online";

export function getScenario(id: string): MockScenario | undefined {
  return MOCK_SCENARIOS.find((s) => s.id === id);
}

export function isMockScenarioId(value: string): value is MockScenarioId {
  return MOCK_SCENARIOS.some((s) => s.id === value);
}
